import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type PropsWithChildren,
  type RefObject,
} from 'react';
import { sendReward } from '@dx-ui/framework-conductrics';

import { useHeaderContext } from './header-context';
import { useActiveUserLinks } from '../hooks/useActiveUserLinks';
import { sendInteractionReward, getLogoVariantFromThemeColors } from '../header.utilities';
import type { HeaderLogoVariant } from '../types';
import { HEADER_VARIANT_TYPE } from '../header.constants';

const PROFILE_NAME_CLICK_GOAL = 'profile-name-click';

/**
 * Menu and drawer state
 */
export type HeaderMenuState = {
  isMenuOpen: boolean;
  setIsMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isAccountOpen: boolean;
  setIsAccountOpen: React.Dispatch<React.SetStateAction<boolean>>;
  closeMenus: () => void;
};

/**
 * Button refs for focus management
 */
export type HeaderRefs = {
  userLoggedInButtonRef: RefObject<HTMLButtonElement | null>;
  userButtonLinkRef: RefObject<HTMLButtonElement | null>;
};

/**
 * Event handlers for user interactions
 */
export type HeaderEventHandlers = {
  menuPressed: () => void;
  accountPressed: () => void;
};

/**
 * Computed display flags based on configuration
 */
export type HeaderDisplayFlags = {
  isMenuItemsAvailable: boolean;
  isGlobalNavAvailable: boolean;
  shouldShowHamburger: boolean;
  displayDesktopHeaderTopLeftLogo: boolean;
  displayDesktopHeaderTopLeftHamburger: boolean;
  showSignedOutAuthCluster: boolean;
};

/**
 * Styling-related computed values
 */
export type HeaderStyling = {
  /** Resolved HeaderLogo image variant for the global nav / mobile drawer logo */
  logoVariant: HeaderLogoVariant;
  /** Resolved HeaderLogo image variant for the desktop bottom nav bar logo */
  bottomNavLogoVariant: HeaderLogoVariant;
};

/**
 * Accessibility props for heading elements
 */
export type HeadingLevelProps =
  | {
      role: 'heading';
      'aria-level': 1;
      'aria-label': string;
    }
  | Record<string, never>;

/**
 * Complete header state context value
 */
export type HeaderStateContextValue = {
  menuState: HeaderMenuState;
  refs: HeaderRefs;
  handlers: HeaderEventHandlers;
  displayFlags: HeaderDisplayFlags;
  styling: HeaderStyling;
  headingLevelProps: HeadingLevelProps;
  activeUserLinks: ReturnType<typeof useActiveUserLinks>;
};

const HeaderStateContext = createContext<HeaderStateContextValue | null>(null);

/**
 * Hook to access header state context values
 * @throws Error if used outside of HeaderStateProvider
 */
export const useHeaderStateContext = () => {
  const context = useContext(HeaderStateContext);
  if (!context) {
    throw new Error('useHeaderStateContext must be used within a HeaderStateProvider');
  }
  return context;
};

/**
 * Provider component for header state
 * Manages all interactive state and computed values for header components
 */
export const HeaderStateProvider = ({ children }: PropsWithChildren) => {
  const {
    visual: { surfaceType, suppressLogo, heading },
    features: {
      shouldSuppressJoinAndSignIn,
      shouldSuppressB2BLink,
      shouldSuppressGoHiltonLink,
      shouldSuppressHGVMaxLink,
      shouldSuppressFriendsAndFamilyLink,
    },
    navigation: {
      menuItems,
      globalNavLinks,
      headerVariantType,
      navHeaderLinksLogoSurfaceType,
      navHeaderLinksLogoColors,
      navHeaderLinksLogoVariant,
      globalNavLogoSurfaceType,
      globalNavLogoColors,
      globalNavLogoVariant,
    },
  } = useHeaderContext();

  // Menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);

  // Refs for focus management
  const userLoggedInButtonRef = useRef<HTMLButtonElement>(null);
  const userButtonLinkRef = useRef<HTMLButtonElement>(null);

  // User links - computed internally based on auth state and feature flags
  const activeUserLinks = useActiveUserLinks({
    featureFlags: {
      shouldSuppressB2BLink,
      shouldSuppressGoHiltonLink,
      shouldSuppressHGVMaxLink,
      shouldSuppressFriendsAndFamilyLink,
    },
  });

  // Close all menus
  const closeMenus = useCallback(() => {
    setIsMenuOpen(false);
    setIsAccountOpen(false);
  }, []);

  // Handle Escape key to close menus
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenus();
      }
    };

    if (isMenuOpen || isAccountOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isMenuOpen, isAccountOpen, closeMenus]);

  // Uses `resize` + `window.innerWidth` deliberately, not `matchMedia`. When an open drawer
  // sets `overflow: hidden`, Safari can recompute the layout viewport as the scrollbar
  // disappears, making a `min-width: 1024px` media query flip to `matches: true` at a real
  // 1023px window, which would flash the menu open then instantly close it.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) closeMenus();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [closeMenus]);

  // Event handlers
  const menuPressed = useCallback(() => {
    sendInteractionReward();
    if (isAccountOpen) setIsAccountOpen(false);
    setIsMenuOpen((prev) => !prev);
  }, [isAccountOpen]);

  const accountPressed = useCallback(() => {
    sendInteractionReward();
    sendReward(PROFILE_NAME_CLICK_GOAL);
    if (isMenuOpen) setIsMenuOpen(false);
    setIsAccountOpen((prev) => !prev);
  }, [isMenuOpen]);

  // Computed display flags
  const isMenuItemsAvailable = !!(menuItems && menuItems.length > 0);
  const isGlobalNavAvailable = !!(globalNavLinks && globalNavLinks.length > 0);

  const displayDesktopHeaderTopLeftLogo =
    headerVariantType === HEADER_VARIANT_TYPE.default ||
    headerVariantType === HEADER_VARIANT_TYPE.minimal ||
    !suppressLogo;

  const displayDesktopHeaderTopLeftHamburger =
    headerVariantType === HEADER_VARIANT_TYPE.hamburgerNav ||
    headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimal ||
    headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimalReimagine;

  const shouldShowHamburger =
    ((isMenuItemsAvailable || isGlobalNavAvailable) && displayDesktopHeaderTopLeftHamburger) ||
    (isGlobalNavAvailable && headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimal) ||
    (isGlobalNavAvailable &&
      headerVariantType === HEADER_VARIANT_TYPE.hamburgerNavMinimalReimagine);

  const showSignedOutAuthCluster =
    !!(activeUserLinks?.signInLink || activeUserLinks?.signUpLink) && !shouldSuppressJoinAndSignIn;

  /**
   * Logo variant computations. `navHeaderLinks` tokens apply ONLY to the desktop bottom
   * nav logo; when absent, it falls back through the global nav's static surface prop.
   */
  const globalNavFallbackSurface = globalNavLogoSurfaceType ?? surfaceType;

  const logoVariant =
    globalNavLogoVariant ??
    getLogoVariantFromThemeColors({
      text: globalNavLogoColors?.text,
      background: globalNavLogoColors?.background,
      fallbackSurface: globalNavFallbackSurface,
    });

  const bottomNavLogoVariant =
    navHeaderLinksLogoVariant ??
    getLogoVariantFromThemeColors({
      text: navHeaderLinksLogoColors?.text,
      background: navHeaderLinksLogoColors?.background,
      fallbackSurface: navHeaderLinksLogoSurfaceType ?? globalNavFallbackSurface,
    });

  // Heading level props for accessibility
  const headingLevelProps: HeadingLevelProps = heading
    ? {
        role: 'heading',
        'aria-level': 1,
        'aria-label': heading,
      }
    : {};

  const contextValue = useMemo<HeaderStateContextValue>(
    () => ({
      menuState: {
        isMenuOpen,
        setIsMenuOpen,
        isAccountOpen,
        setIsAccountOpen,
        closeMenus,
      },
      refs: {
        userLoggedInButtonRef,
        userButtonLinkRef,
      },
      handlers: {
        menuPressed,
        accountPressed,
      },
      displayFlags: {
        isMenuItemsAvailable,
        isGlobalNavAvailable,
        shouldShowHamburger,
        displayDesktopHeaderTopLeftLogo,
        displayDesktopHeaderTopLeftHamburger,
        showSignedOutAuthCluster,
      },
      styling: {
        logoVariant,
        bottomNavLogoVariant,
      },
      headingLevelProps,
      activeUserLinks,
    }),
    [
      isMenuOpen,
      isAccountOpen,
      closeMenus,
      menuPressed,
      accountPressed,
      isMenuItemsAvailable,
      isGlobalNavAvailable,
      shouldShowHamburger,
      displayDesktopHeaderTopLeftLogo,
      displayDesktopHeaderTopLeftHamburger,
      showSignedOutAuthCluster,
      logoVariant,
      bottomNavLogoVariant,
      headingLevelProps,
      activeUserLinks,
    ]
  );

  return <HeaderStateContext.Provider value={contextValue}>{children}</HeaderStateContext.Provider>;
};
